import dayjs from 'dayjs';
import { MonthCoverageDto, OpsBriefingDayState } from '../../types/opsBriefing';

/**
 * 히트맵 칸의 "기록 상태".
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 필요한가
 * ─────────────────────────────────────────────────────────────
 * `days[]` 는 브리핑 문서가 있는 날만 옵니다. 그래서 화면은
 * **"브리핑이 아예 만들어지지 않은 날"과 "챙길 일이 0건이던 날"을 똑같은 빈 칸**
 * 으로 그렸습니다. 원장님은 앞의 경우를 "그날은 평화로웠구나"로 읽습니다.
 * 정반대의 사실입니다.
 *
 * 서버가 `coverage` 로 그 달 1일~말일의 상태를 전부 내려주므로,
 * 여기서 화면이 그릴 수 있는 최소한의 종류로 접습니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 이 파일이 넘지 않는 선
 * ─────────────────────────────────────────────────────────────
 * ① **원인을 주장하지 않는다.** `MISSING` 은 "배치 실패"가 아닙니다.
 *    킬스위치가 꺼져 있었을 수도, 대상에서 빠져 있었을 수도 있고
 *    **서버도 어느 쪽인지 모릅니다.** 화면은 "만들어지지 않았습니다"까지만 씁니다.
 * ② **0 건인 날은 좋은 날이다.** 경고처럼 칠하지 않습니다.
 * ③ **모르는 것은 조용하다.** 기능을 막 켠 센터는 그 달 대부분이
 *    `BEFORE_FIRST_RECORD` 입니다. 그게 빨간 칸으로 가득하면 화면을 닫습니다.
 */
export type HeatCellKind =
    /** days[] 에 기록이 있는 날 — 건수·등급은 기존대로 본다 */
    | 'REPORTED'
    /** 첫 기록 이후인데 문서가 없다. 원인은 모른다 */
    | 'NOT_CREATED'
    /** 문서는 있고 아직 공개 전(검증 중). 실패가 아니다 */
    | 'NOT_PUBLIC'
    /** 문서가 내려졌다 */
    | 'WITHDRAWN'
    /** 첫 기록 이전 · 기록이 하나도 없는 센터 — 말할 수 있는 게 없다 */
    | 'NO_INFO'
    /** 오늘이고 아직 생성 예정 시각 전 */
    | 'PENDING'
    /** 아직 오지 않은 날 */
    | 'FUTURE'
    /** 상태를 모른다 (coverage 를 안 주는 서버, 계약이 어긋난 날) */
    | 'UNKNOWN';

/**
 * 상태 → 칸 종류.
 *
 * `days[]` 에 건수가 있으면 **그게 사실**입니다. coverage 가 뭐라 하든
 * 건수가 있는 날을 빈 칸으로 그리지 않습니다 (두 값은 서버에서 조회 두 번으로
 * 만들어지므로 이론상 어긋날 수 있고, 어긋나면 관측된 쪽을 믿습니다).
 *
 * 반대로 coverage 는 `PUBLISHED` 인데 `days[]` 에 없으면 **건수를 모릅니다.**
 * 그때 "이슈 없음"이라고 쓰면 5건짜리 날을 0건으로 만드는 것이라 `UNKNOWN` 입니다.
 */
export const cellKindOf = (
    state: OpsBriefingDayState | undefined,
    hasReport: boolean
): HeatCellKind => {
    if (hasReport) return 'REPORTED';
    switch (state) {
        case 'MISSING':
            return 'NOT_CREATED';
        case 'SHADOW':
            return 'NOT_PUBLIC';
        case 'ARCHIVED':
            return 'WITHDRAWN';
        case 'BEFORE_FIRST_RECORD':
        case 'NO_RECORD_EVER':
            return 'NO_INFO';
        case 'PENDING':
            return 'PENDING';
        case 'FUTURE':
            return 'FUTURE';
        /* 문서가 공개됐다는데 건수가 없다 — 아는 척하지 않는다 */
        case 'PUBLISHED':
        default:
            return 'UNKNOWN';
    }
};

export type CellMeta = {
    /** 칸 안에 쓰는 짧은 말. 빈 문자열이면 아무 말도 하지 않는다 */
    label: string;
    /** 마우스를 올렸을 때 나오는 설명 */
    note: string;
    /** 범례에 쓰는 이름. 없으면 범례에 올리지 않는다 */
    legend: string;
    bg: string;
    border: string;
    text: string;
};

/*
 * 색을 늘리지 않습니다. "정보 없음"은 회색 계열 한 톤이고,
 * 갈라지는 것은 **무늬와 글자**입니다 —
 *   브리핑 없음 = 빗금(문서가 없다는 뜻의 텍스처)
 *   공개 전/내려감 = 무늬 없는 옅은 면 (문서는 있다)
 *   기록 시작 전 = 거의 백지
 * 색맹 여부와 무관하게 갈라지고, 어느 것도 경고색이 아닙니다.
 */
const HATCH = 'repeating-linear-gradient(45deg, #EDF0F5 0 5px, #FFFFFF 5px 10px)';

export const CELL_META: Record<Exclude<HeatCellKind, 'REPORTED'>, CellMeta> = {
    NOT_CREATED: {
        label: '브리핑 없음',
        // 원인을 말하지 않는다. 서버도 모른다.
        note: '이날은 브리핑이 만들어지지 않았습니다',
        legend: '브리핑이 만들어지지 않은 날',
        bg: HATCH,
        border: '1px solid #E1E7F0',
        text: '#8A94A3',
    },
    NOT_PUBLIC: {
        label: '공개 전',
        note: '이날 브리핑은 아직 공개 전입니다 (내용은 오지 않습니다)',
        legend: '아직 공개 전인 날',
        bg: '#F2F5FB',
        border: '1px solid #DEE6F2',
        text: '#7C8CA5',
    },
    WITHDRAWN: {
        label: '내려감',
        note: '이날 브리핑은 내려졌습니다',
        legend: '브리핑이 내려간 날',
        bg: '#FAFAFC',
        border: '1px solid #E7E9EF',
        text: '#9AA2AE',
    },
    NO_INFO: {
        // 조용해야 한다. 막 켠 센터는 그 달 대부분이 이 상태다.
        label: '',
        note: '기록이 시작되기 전입니다',
        legend: '기록이 시작되기 전',
        bg: '#FFFFFF',
        border: '1px solid #F1F4F9',
        text: '#CFD6E0',
    },
    PENDING: {
        label: '생성 전',
        note: '오늘 브리핑은 아직 만들어지기 전입니다',
        legend: '',
        bg: '#FFFFFF',
        border: '1px dashed #CBD9F0',
        text: '#8AA0C0',
    },
    FUTURE: {
        label: '',
        note: '아직 오지 않은 날',
        legend: '',
        bg: '#FFFFFF',
        border: '1px dashed #E7EDF6',
        text: '#C4CCD8',
    },
    UNKNOWN: {
        label: '',
        // "없었다"고도 "있었다"고도 하지 않는다
        note: '이날 기록은 확인할 수 없습니다',
        legend: '',
        bg: '#FFFFFF',
        border: '1px solid #F1F4F9',
        text: '#CFD6E0',
    },
};

/** 날짜 → 상태. coverage 가 없으면 빈 사전이고, 화면은 전부 UNKNOWN 으로 본다 */
export const stateByDateOf = (
    coverage: MonthCoverageDto | null | undefined
): Record<string, OpsBriefingDayState> => {
    const m: Record<string, OpsBriefingDayState> = {};
    coverage?.days?.forEach((d) => {
        m[d.date] = d.state;
    });
    return m;
};

/* ───────────────────────── 한 달 요약 ───────────────────────── */

export type CoverageSummary = {
    monthKey: string;
    /** 서버가 coverage 를 줬는가. 아니면 아래 숫자로 아무 말도 하면 안 된다 */
    hasCoverage: boolean;
    /** 문서가 있던 날 (공개·공개 전·내려감) */
    recordedDays: number;
    publishedDays: number;
    shadowDays: number;
    archivedDays: number;
    /** 첫 기록 이후인데 브리핑이 없던 날 */
    notCreatedDays: number;
    /** 첫 기록 이전 · 기록 없는 센터 */
    noInfoDays: number;
    /** 브리핑이 올라온 날 (items > 0) — 실행률·요일 패턴의 분모가 되는 날 */
    activeDays: number;
    /** 문서가 있고 항목이 0 건이던 날 — 좋은 날이다 */
    zeroIssueDays: number;
    /** 이 달에 그릴 것이 있는가 (없으면 빈 화면으로 빠진다) */
    worthShowing: boolean;
    /** 히트맵 아래 한 줄. 할 말이 없으면 빈 문자열 */
    note: string;
    /** 실행률 숫자 옆에 붙일 꼬리. 없으면 빈 문자열 */
    denomTail: string;
};

/**
 * 그 달의 기록 상태 요약.
 *
 * **오늘까지만 셉니다.** 아직 오지 않은 날은 브리핑이 없는 게 당연한데
 * 그걸 "만들어지지 않은 날"로 세면 8월 3일에 "28일치가 없습니다"가 찍힙니다.
 * 오늘은 서버가 준 값(`coverage.today`)을 씁니다 — 브라우저 시계로 정하면
 * KST 가 아닌 기기에서 하루가 어긋납니다.
 */
export const summarizeCoverage = (
    coverage: MonthCoverageDto | null | undefined,
    reports: { date: string; items: number }[],
    monthKey: string
): CoverageSummary => {
    const activeDays = reports.filter((d) => d.date.slice(0, 7) === monthKey && d.items > 0).length;
    const hasReport = new Set(reports.map((d) => d.date));

    const base: CoverageSummary = {
        monthKey,
        hasCoverage: false,
        recordedDays: 0,
        publishedDays: 0,
        shadowDays: 0,
        archivedDays: 0,
        notCreatedDays: 0,
        noInfoDays: 0,
        activeDays,
        zeroIssueDays: 0,
        /* coverage 가 없으면 예전 기준 그대로 — 올라온 날이 있어야 그린다 */
        worthShowing: activeDays > 0,
        note: '',
        denomTail: '',
    };
    if (!coverage || !coverage.days?.length) return base;

    const today = coverage.today;
    let publishedDays = 0;
    let shadowDays = 0;
    let archivedDays = 0;
    let notCreatedDays = 0;
    let noInfoDays = 0;
    let zeroIssueDays = 0;

    coverage.days.forEach((d) => {
        // 오늘 이후는 세지 않는다 (FUTURE, 그리고 아직 생성 전인 오늘)
        if (d.date > today) return;
        switch (d.state) {
            case 'PUBLISHED':
                publishedDays += 1;
                if (hasReport.has(d.date)) {
                    const row = reports.find((r) => r.date === d.date);
                    if (row && row.items === 0) zeroIssueDays += 1;
                }
                break;
            case 'SHADOW':
                shadowDays += 1;
                break;
            case 'ARCHIVED':
                archivedDays += 1;
                break;
            case 'MISSING':
                notCreatedDays += 1;
                break;
            case 'BEFORE_FIRST_RECORD':
            case 'NO_RECORD_EVER':
                noInfoDays += 1;
                break;
            default:
                break;
        }
    });

    const recordedDays = publishedDays + shadowDays + archivedDays;
    const month = dayjs(`${monthKey}-01`).format('M월');

    /*
     * 한 줄 안내.
     *
     * 여기서 말하는 것은 딱 둘입니다 — **무엇이 없었는가**, 그리고
     * **위 숫자가 며칠을 센 것인가.** 원인은 말하지 않습니다.
     * 아무 일도 없었으면 아무 말도 하지 않습니다. 매달 뜨는 안내는 안 읽힙니다.
     */
    const parts: string[] = [];
    if (notCreatedDays > 0) parts.push(`${month}에는 브리핑이 만들어지지 않은 날이 ${notCreatedDays}일 있습니다`);
    if (shadowDays > 0) parts.push(`아직 공개 전인 날이 ${shadowDays}일 있습니다`);
    if (archivedDays > 0) parts.push(`내려간 날이 ${archivedDays}일 있습니다`);

    let note = '';
    if (parts.length) {
        /*
         * 분모를 밝히는 꼬리는 셀 것이 있을 때만 붙인다.
         * 한 달 내내 브리핑이 없던 달에 "올라온 0일만 셉니다"는 말이 안 된다.
         */
        note = parts.join(' · ') + (activeDays > 0
            ? ` · 위 실행률과 요일 패턴은 브리핑이 올라온 ${activeDays}일만 셉니다`
            : '');
    } else if (noInfoDays > 0 && recordedDays > 0 && coverage.firstBriefingDate) {
        // 빠진 날은 없다. 다만 이 달 앞쪽이 왜 비어 있는지는 밝힌다 — 조용하게.
        note = `이 센터의 기록은 ${dayjs(coverage.firstBriefingDate).format('M월 D일')}부터 남아 있습니다`;
    }

    const denomTail = notCreatedDays > 0 ? ` · 브리핑이 없던 ${notCreatedDays}일 제외` : '';

    return {
        ...base,
        hasCoverage: true,
        recordedDays,
        publishedDays,
        shadowDays,
        archivedDays,
        notCreatedDays,
        noInfoDays,
        zeroIssueDays,
        /*
         * 그릴 것이 있는가.
         *
         * 예전에는 '올라온 항목이 있는 날'만 봤습니다. 그래서 브리핑이 한 달 내내
         * 만들어지지 않은 달도, 챙길 일이 0 건이던 날만 있던 달도 똑같이
         * "아직 지난 기록이 없습니다" 로 접혔습니다 — 둘 다 사실이 아닙니다.
         */
        worthShowing: activeDays > 0 || recordedDays > 0 || notCreatedDays > 0,
        note,
        denomTail,
    };
};

/** 범례에 올릴 종류 — 그 달에 실제로 있는 것만. 안 쓰는 색을 설명하면 범례가 죽는다 */
export const legendKindsOf = (s: CoverageSummary): Exclude<HeatCellKind, 'REPORTED'>[] => {
    if (!s.hasCoverage) return [];
    const out: Exclude<HeatCellKind, 'REPORTED'>[] = [];
    if (s.notCreatedDays > 0) out.push('NOT_CREATED');
    if (s.shadowDays > 0) out.push('NOT_PUBLIC');
    if (s.archivedDays > 0) out.push('WITHDRAWN');
    if (s.noInfoDays > 0) out.push('NO_INFO');
    return out;
};
